import { Typography } from "@lyfegen-platform/react-common";
import { Box, Divider, Paper, useTheme } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import LyfeTooltip from "../../../components/LyfeTooltip";
import useStyles from "../use-styles";
import renderCurrency from "../../../utils/currency";
import { EMPTY_VALUE_PLACEHOLDER } from "../../../models/common/data";
import Adjust from "../../../components/icons/Adjust";

interface IDetailsCard {
	headersData: any;
	isDrawerOpen: boolean;
}

export const getDiscountsValue = (amount: any, code: any) => {
	return isNaN(+amount) ? amount : renderCurrency(amount, code, 2, 2);
};

export const DetailsCard = (props: IDetailsCard) => {
	const { headerDetailsCard, fw500, detailsItem, detailsItemBox, detailsItemBoxShrunk, detailsItemShrunk, fw400 } = useStyles();
	const { t } = useTranslation();
	const {
		typography: { pxToRem },
		spacing,
		palette,
	} = useTheme();
	const currencyName = t(props.headersData.currency.Name || "") || "";
	const hiddenValueMask = "XX";

	const refundable = getDiscountsValue(props.headersData.refundableAmount, props.headersData.currency.Code);
	const identified = getDiscountsValue(props.headersData.newAmount, props.headersData.currency.Code);
	const invoiced = getDiscountsValue(props.headersData.invoicedAmount, props.headersData.currency.Code);
	const settled = getDiscountsValue(props.headersData.settledAmount, props.headersData.currency.Code);
	const declinedAmount = getDiscountsValue(props.headersData.declinedAmount, props.headersData.currency.Code);
	const pendingAmount = getDiscountsValue(props.headersData.pendingAmount, props.headersData.currency.Code);

	return (
		<Paper className={headerDetailsCard}>
			<Box width={"30%"}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography className={fw400} color="textPrimary" type="h7" variant="body1">
						{t("discounts").toUpperCase()}
					</Typography>
					<Divider style={{ margin: pxToRem(15), flexGrow: 1 }} />
				</Box>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					{props.headersData.isAdjusted && (
						<LyfeTooltip title={t("adjusted-amount-total-hover")}>
							<span
								style={{
									verticalAlign: "middle",
									backgroundColor: "#ebf2f9",
									borderRadius: "4px",
									height: "24px",
									width: "24px",
									display: "inline-block",
									marginRight: spacing(0.75),
								}}
							>
								<Adjust fill={palette.grey[500]} />
							</span>
						</LyfeTooltip>
					)}
					<LyfeTooltip title={refundable !== hiddenValueMask ? currencyName : ""}>
						<Typography noWrap style={{ fontSize: pxToRem(18) }} variant="h6">
							{refundable || EMPTY_VALUE_PLACEHOLDER}
						</Typography>
					</LyfeTooltip>
				</Box>
			</Box>
			<Divider orientation="vertical" style={{ margin: pxToRem(15) }} />

			<Box display="flex" justifyContent="space-between" className={props.isDrawerOpen ? detailsItemBoxShrunk : detailsItemBox}>
				{props.headersData.newAmount > 0 && (
					<Box className={`${props.isDrawerOpen ? detailsItemShrunk : detailsItem}`}>
						<Typography noWrap className={fw400} variant="body2">
							{t("identified").toUpperCase()}
						</Typography>
						&nbsp;
						<LyfeTooltip title={identified !== hiddenValueMask ? currencyName : ""}>
							<div>
								<Typography noWrap className={fw500} variant="body2">
									{identified || EMPTY_VALUE_PLACEHOLDER}
								</Typography>
							</div>
						</LyfeTooltip>
					</Box>
				)}
				{props.headersData.invoicedAmount > 0 && (
					<Box className={`${props.isDrawerOpen ? detailsItemShrunk : detailsItem}`}>
						<Typography noWrap className={fw400} variant="body2">
							{t("invoiced").toUpperCase() || EMPTY_VALUE_PLACEHOLDER}
						</Typography>
						&nbsp;
						<LyfeTooltip title={invoiced !== hiddenValueMask ? currencyName : ""}>
							<div>
								<Typography noWrap className={fw500} variant="body2">
									{invoiced || EMPTY_VALUE_PLACEHOLDER}
								</Typography>
							</div>
						</LyfeTooltip>
					</Box>
				)}

				{props.headersData.pendingAmount > 0 && (
					<Box className={`${props.isDrawerOpen ? detailsItemShrunk : detailsItem}`}>
						<Typography noWrap className={fw400} variant="body2">
							{t("pending").toUpperCase() || "--"}
						</Typography>
						&nbsp;
						<LyfeTooltip title={pendingAmount !== hiddenValueMask ? currencyName : ""}>
							<div>
								<Typography noWrap className={fw500} variant="body2">
									{pendingAmount || "--"}
								</Typography>
							</div>
						</LyfeTooltip>
					</Box>
				)}
				{props.headersData.settledAmount > 0 && (
					<Box className={`${props.isDrawerOpen ? detailsItemShrunk : detailsItem}`}>
						<Typography noWrap className={fw400} variant="body2">
							{t("settled").toUpperCase() || EMPTY_VALUE_PLACEHOLDER}
						</Typography>
						&nbsp;
						<LyfeTooltip title={settled !== hiddenValueMask ? currencyName : ""}>
							<div>
								<Typography noWrap className={fw500} variant="body2">
									{settled || EMPTY_VALUE_PLACEHOLDER}
								</Typography>
							</div>
						</LyfeTooltip>
					</Box>
				)}
				{props.headersData.declinedAmount > 0 && (
					<Box className={`${props.isDrawerOpen ? detailsItemShrunk : detailsItem}`}>
						<Typography noWrap className={fw400} variant="body2">
							{t("declined").toUpperCase() || EMPTY_VALUE_PLACEHOLDER}
						</Typography>
						&nbsp;
						<LyfeTooltip title={declinedAmount !== hiddenValueMask ? currencyName : ""}>
							<div>
								<Typography noWrap className={fw500} variant="body2">
									{declinedAmount || EMPTY_VALUE_PLACEHOLDER}
								</Typography>
							</div>
						</LyfeTooltip>
					</Box>
				)}
			</Box>
		</Paper>
	);
};
